import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Share2, Calendar, User, Copy, Check } from 'lucide-react';
import { generateShareableLink, trackDeepLinkUsage } from '@/utils/deepLinkUtils';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const ShareTimeline = () => {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('timeline');
  const [copied, setCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState<string>('');

  const entryIds = searchParams.get('entries')?.split(',') || [];

  // Track deep link usage
  useEffect(() => {
    if (username) {
      trackDeepLinkUsage('share_timeline', 'view', { username, entryIds });
    }
  }, [username, entryIds]);

  // Fetch profile data
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', username],
    queryFn: async () => {
      if (!username) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!username
  });

  // Fetch entries to share
  const { data: entries, isLoading: entriesLoading } = useQuery({
    queryKey: ['share-entries', profile?.id, entryIds],
    queryFn: async () => {
      if (!profile?.id) return [];
      
      let query = supabase
        .from('profile_timeline_entries')
        .select(`
          *,
          profile_timeline_sections!inner(
            name,
            name_en,
            profile_timeline!inner(user_id)
          )
        `)
        .eq('profile_timeline_sections.profile_timeline.user_id', profile.id)
        .eq('privacy_level', 'public'); // Only public entries can be shared

      if (entryIds.length > 0) {
        query = query.in('id', entryIds);
      }

      const { data, error } = await query.order('date', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id
  });

  // Generate shareable link
  useEffect(() => {
    const generateLink = async () => {
      if (profile?.id) {
        const { link } = await generateShareableLink('timeline', profile.id, {
          title: `${profile.first_name} ${profile.last_name}'s Timeline`,
          description: `Check out memories from ${profile.first_name}'s timeline on Uplivion`,
          includePreview: true
        });
        setShareableLink(link);
      }
    };
    
    generateLink();
  }, [profile]);

  const handleCopyLink = async () => {
    if (shareableLink) {
      try {
        await navigator.clipboard.writeText(shareableLink);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy link');
      }
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share && shareableLink) {
      try {
        await navigator.share({
          title: `${profile?.first_name} ${profile?.last_name}'s Timeline`,
          text: `Check out memories from ${profile?.first_name}'s timeline on Uplivion`,
          url: shareableLink
        });
        
        trackDeepLinkUsage('share_timeline', 'native_share', { username });
      } catch (error) {
        console.log('Native share cancelled or failed');
      }
    }
  };

  if (profileLoading || entriesLoading) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Loading timeline...</div>
        </div>
      </AppShell>
    );
  }

  if (!profile) {
    return (
      <AppShell 
        showHeader={false} 
        showNavigation={false}
        backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
      >
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center min-h-[80vh]">
          <div className="text-center">Timeline not found</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell 
      showHeader={true} 
      showNavigation={false}
      backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)"
    >
      <div className="container mx-auto py-8 px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="mb-4 w-16 h-16 rounded-full bg-uplivion-blue/10 flex items-center justify-center mx-auto">
            <Share2 className="h-8 w-8 text-uplivion-blue" />
          </div>
          <h1 className="text-3xl font-bold text-uplivion-blue mb-2">
            Share Timeline
          </h1>
          <p className="text-gray-600">
            {profile.first_name} {profile.last_name}'s Timeline
          </p>
        </div>

        <Card className="uplivion-card mb-6">
          <CardHeader>
            <CardTitle>Timeline Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-uplivion-blue/10 flex items-center justify-center">
                <User className="h-6 w-6 text-uplivion-blue" />
              </div>
              <div>
                <h3 className="font-semibold">{profile.first_name} {profile.last_name}</h3>
                <p className="text-sm text-muted-foreground">@{profile.username}</p>
              </div>
            </div>
            
            {entries && entries.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {entryIds.length > 0 
                    ? `${entries.length} selected memories` 
                    : `${entries.length} public memories`
                  }
                </p>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {entries.slice(0, 3).map((entry) => (
                    <div key={entry.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{entry.title}</p>
                        {entry.date && (
                          <p className="text-xs text-gray-500">
                            {new Date(entry.date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {entries.length > 3 && (
                    <p className="text-xs text-gray-500 text-center">
                      And {entries.length - 3} more memories...
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="uplivion-card">
          <CardHeader>
            <CardTitle>Share Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <input
                type="text"
                value={shareableLink}
                readOnly
                className="flex-1 bg-transparent border-none outline-none text-sm"
                placeholder="Generating link..."
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyLink}
                disabled={!shareableLink}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {navigator.share && (
                <Button
                  onClick={handleNativeShare}
                  disabled={!shareableLink}
                  className="w-full"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={() => window.open(`/timeline/${username}`, '_blank')}
                className="w-full"
              >
                View Timeline
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
};

export default ShareTimeline;